import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { Plane, Plus, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";

function TripDateField({ field }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <label className="text-sm font-medium text-[#0A110E]">{field.label}</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid={field.tid}
            type="button"
            className="w-full flex items-center gap-2 px-3 h-10 rounded-md border border-input bg-background text-sm text-left"
          >
            <CalendarIcon className="w-4 h-4 text-[#8A9992]" />
            <span className={field.val ? "text-[#0A110E]" : "text-[#8A9992]"}>
              {field.val ? format(field.val, "PPP") : "Fecha"}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white" align="start">
          <Calendar
            mode="single"
            selected={field.val}
            onSelect={(d) => { field.set(d); setOpen(false); }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function TripDialog({ open, onOpenChange, onSaved }) {
  const [name, setName] = useState("");
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setName(""); setStart(null); setEnd(null); }
  }, [open]);

  const submit = async () => {
    if (!name.trim()) return toast.error("Pon un nombre");
    if (!start || !end) return toast.error("Selecciona fechas");
    if (end < start) return toast.error("La fecha final debe ser posterior");
    setSaving(true);
    try {
      await api.post("/trips", {
        name,
        start_date: format(start, "yyyy-MM-dd"),
        end_date: format(end, "yyyy-MM-dd"),
      });
      toast.success("Viaje añadido · esos gastos quedan fuera del análisis");
      onSaved?.();
      onOpenChange(false);
    } catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Modo vacaciones</DialogTitle>
          <p className="text-sm text-[#5F7368]">Los gastos de este rango se excluyen del análisis mensual y presupuestos.</p>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div>
            <label className="text-sm font-medium text-[#0A110E]">Nombre</label>
            <Input data-testid="trip-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Escapada a París" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Desde", val: start, set: setStart, tid: "trip-start", key: "start" },
              { label: "Hasta", val: end, set: setEnd, tid: "trip-end", key: "end" },
            ].map((f) => (
              <TripDateField key={f.key} field={f} />
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="trip-save-btn" onClick={submit} disabled={saving} className="bg-[#0F291E] hover:bg-[#1C7C54]">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Trips() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try { setItems((await api.get("/trips")).data); }
    catch (e) { logger.error("trips load", e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try { await api.delete(`/trips/${id}`); toast.success("Viaje eliminado"); load(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-end justify-between gap-4 fade-up">
        <div>
          <p className="uppercase text-xs tracking-[0.2em] text-[#8A9992] mb-2 flex items-center gap-2">
            <Plane className="w-3 h-3" /> {t("trips.kicker")}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A110E]">{t("trips.title")}</h1>
          <p className="text-[#5F7368] mt-2">{t("trips.subtitle", { defaultValue: "Los gastos en estas fechas no cuentan en tus presupuestos ni gráficos mensuales." })}</p>
        </div>
        <Button data-testid="add-trip-btn" onClick={() => setOpen(true)} className="bg-[#0F291E] hover:bg-[#1C7C54] rounded-xl">
          <Plus className="w-4 h-4 mr-2" /> {t("trips.newTrip", { defaultValue: "Nuevo viaje" })}
        </Button>
      </div>

      <div className="card-surface p-6">
        {loading ? (
          <div className="text-[#5F7368]">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-[#8A9992]">Aún no hay viajes marcados.</div>
        ) : (
          <div className="divide-y divide-[#EBEBEA]" data-testid="trips-list">
            {items.map((t) => (
              <div key={t.id} data-testid={`trip-${t.id}`} className="flex items-center py-3 gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#1C7C54]/10 flex items-center justify-center">
                  <Plane className="w-4 h-4 text-[#1C7C54]" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-[#0A110E]">{t.name}</div>
                  <div className="text-xs text-[#8A9992] tabular-nums">
                    {format(parseISO(t.start_date), "dd MMM yyyy")} — {format(parseISO(t.end_date), "dd MMM yyyy")}
                  </div>
                </div>
                <button onClick={() => remove(t.id)} data-testid={`del-trip-${t.id}`} className="p-2 rounded-lg hover:bg-[#EBEBEA]">
                  <Trash2 className="w-4 h-4 text-[#D95D39]" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <TripDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}
